/**
 * Material Dropdown Component with enhanced error handling
 *
 * Features:
 * - Custom error messages support
 * - Form control integration
 * - Search functionality
 * - Pagination support
 * - Prefix icons/images
 *
 * Usage Examples:
 *
 * 1. Basic usage with custom error message:
 * <app-mat-dropdown
 *   [control]="form.get('vehicleType')"
 *   customErrorMessages="Please select a valid vehicle type"
 *   label="Vehicle Type"
 *   [options]="vehicleOptions">
 * </app-mat-dropdown>
 *
 * 2. With specific error message:
 * <app-mat-dropdown
 *   [control]="form.get('driver')"
 *   errorMessage="Driver selection is required"
 *   label="Select Driver"
 *   [options]="driverOptions">
 * </app-mat-dropdown>
 *
 * 3. Using common error messages from FormErrorMessages:
 * <app-mat-dropdown
 *   [control]="form.get('vehicleType')"
 *   [customErrorMessages]="getFieldErrorMessage('vehicleType')"
 *   label="Vehicle Type"
 *   [options]="vehicleOptions">
 * </app-mat-dropdown>
 */
import { Component, EventEmitter, Input, Output, ViewChild, forwardRef, OnInit, Injector, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, FormControl, NgControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface DropdownOption {
  value: any;
  label: string;
  disabled?: boolean;
  icon?: string;
}

export interface PrefixConfig {
  type: 'icon' | 'image';
  value: string;
  alt?: string;
  size?: number;
  color?: string;
}

export interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Component({
  selector: 'app-mat-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatButtonModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatDropdownComponent),
      multi: true
    }
  ],
  templateUrl: './mat-dropdown.component.html',
  styleUrl: './mat-dropdown.component.scss'
})
export class MatDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @ViewChild('select') select: any;

  private defaultControl = new FormControl('');
  private _control: AbstractControl | null = null;

  @Input() options: DropdownOption[] = [];
  @Input() label = '';
  @Input() placeholder = 'Select an option';
  @Input() value: any = null;

  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }
  get disabled(): boolean {
    return this._disabled;
  }
  private _disabled = false;

  @Input() required = false;
  @Input() multiple = false;
  @Input() appearance: 'fill' | 'outline' = 'outline';
  @Input() hint = '';
  @Input() errorMessage = '';
  @Input() showClearButton = true;
  @Input() width = '100%';
  @Input() searchable = false;
  @Input() loading = false;
  @Input() minSearchLength = 2;
  @Input() searchPlaceholder = 'Search...';
  @Input() enablePagination = false;
  @Input() pageSize = 10;
  @Input() prefix: PrefixConfig | null = null;
  @Input() prefixAlign: 'start' | 'center' | 'end' = 'start';
  @Input() prefixClass = '';
  @Input() prefixStyle = '';
  @Input() prefixContainerClass = '';
  @Input() prefixContainerStyle = '';
  @Input() convertToUppercase = false;

  private _customErrorMessages: string = '';
  @Input()
  set customErrorMessages(value: string) {
    this._customErrorMessages = value;
  }
  get customErrorMessages(): string {
    return this._customErrorMessages;
  }

  @Input() paginationInfo: PaginationInfo = {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  };

  @Output() selectionChange = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() search = new EventEmitter<{ searchText: string; page: number; pageSize: number }>();
  @Output() pageChange = new EventEmitter<{ page: number; pageSize: number }>();

  filteredOptions: DropdownOption[] = [];
  searchText = '';
  private searchSubject = new Subject<string>();
  isInitialLoad = true;

  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private injector: Injector) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchText => {
      if (searchText.length >= this.minSearchLength || searchText.length === 0) {
        this.paginationInfo.currentPage = 1;
        this.search.emit({
          searchText,
          page: this.paginationInfo.currentPage,
          pageSize: this.pageSize
        });
      }
    });
  }

  @Input() set control(value: AbstractControl | null) {
    this._control = value || this.defaultControl;
    // Subscribe to value changes when control is set
    if (this._control) {
      this._control.valueChanges.subscribe(val => {
        if (val !== this.value) {
          this.value = val;
        }
      });
    }
  }
  get control(): FormControl {
    return (this._control as FormControl) || this.defaultControl;
  }

    ngOnInit() {
    // Try to get the control from NgControl if available
    try {
      const ngControl = this.injector.get(NgControl, null);

      if (ngControl && ngControl.control) {
        this._control = ngControl.control;
        ngControl.valueAccessor = this;
      } else if (!this._control) {
        this._control = this.defaultControl;
      }
    } catch (error) {
      // If NgControl is not available, use default control
      if (!this._control) {
        this._control = this.defaultControl;
      }
    }

    this.filteredOptions = [...this.options];
    if (this.enablePagination && this.isInitialLoad) {
      this.loadInitialData();
    }
  }

  ngOnDestroy() {
    if (this.searchSubject) {
      this.searchSubject.unsubscribe();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.options) {
      this.filteredOptions = [...this.options];
      this.isInitialLoad = false;
    }
    if (changes.paginationInfo) {
      this.paginationInfo = { ...this.paginationInfo };
    }
  }

  getErrorMessage(): string {
    if (!this._control) return '';
    const errors = this._control.errors;
    if (!errors) return '';

    // Return custom error message if provided
    if (this.customErrorMessages) {
      return this.customErrorMessages;
    }

    // Check for specific error message
    if (this.errorMessage) {
      return this.errorMessage;
    }

    // If no custom messages, return a default error message
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      return 'Invalid selection';
    }

    return '';
  }

  loadInitialData() {
    this.search.emit({
      searchText: '',
      page: 1,
      pageSize: this.pageSize
    });
  }

  onSearchChange(event: Event) {
    let searchText = (event.target as HTMLInputElement).value;

    // Convert to uppercase if the convertToUppercase flag is enabled
    if (this.convertToUppercase) {
      searchText = searchText.toUpperCase();
      // Update the input field value to show uppercase
      (event.target as HTMLInputElement).value = searchText;
    }

    this.searchText = searchText;
    this.searchSubject.next(searchText);

    if (!this.search.observed && !this.enablePagination) {
      this.filteredOptions = this.options.filter(option =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  }

  onPageChange(page: number) {
    if (page === this.paginationInfo.currentPage) return;

    this.paginationInfo.currentPage = page;
    if (this.searchText) {
      this.search.emit({
        searchText: this.searchText,
        page: this.paginationInfo.currentPage,
        pageSize: this.pageSize
      });
    } else {
      this.pageChange.emit({
        page: this.paginationInfo.currentPage,
        pageSize: this.pageSize
      });
    }
  }

  onSelectionChange(event: any) {
    if (this._disabled) return; // Don't allow selection changes if disabled

    const newValue = event.value;

    // Only update if the value has actually changed
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
      this.onTouched();
      if (this._control) {
        this._control.setValue(this.value, { emitEvent: false });
      }
      this.valueChange.emit(this.value);
      this.selectionChange.emit(event);
    }
  }

  clearSelection() {
    if (this._disabled) return; // Don't allow clearing if disabled

    const newValue = this.multiple ? [] : null;

    // Only update if the value has actually changed
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
      this.onTouched();
      if (this._control) {
        this._control.setValue(this.value, { emitEvent: false });
      }
      this.valueChange.emit(this.value);
      this.selectionChange.emit({ value: this.value });
    }
  }

  onDropdownOpened(opened: boolean) {
    if (opened && this.enablePagination && this.isInitialLoad) {
      this.loadInitialData();
    }
    if (opened) {
      this.searchText = '';
      if (!this.enablePagination) {
        this.filteredOptions = [...this.options];
      }
    }

    this.onTouched();
    // Force mark the control as touched when dropdown is opened
    if (this._control) {
      this._control.markAsTouched();
    }
  }

  get hasNextPage(): boolean {
    return this.paginationInfo.currentPage < this.paginationInfo.totalPages;
  }

  loadMore(): void {
    if (!this.hasNextPage || this.loading) return;

    const nextPage = this.paginationInfo.currentPage + 1;
    this.paginationInfo.currentPage = nextPage;

    if (this.searchText) {
      this.search.emit({
        searchText: this.searchText,
        page: nextPage,
        pageSize: this.pageSize
      });
    } else {
      this.pageChange.emit({
        page: nextPage,
        pageSize: this.pageSize
      });
    }
  }

  get prefixSize(): string {
    if (!this.prefix?.size) return '24px';
    return `${this.prefix.size}px`;
  }

  get prefixStyles(): string {
    const styles: string[] = [];
    if (this.prefix?.color) {
      styles.push(`color: ${this.prefix.color}`);
    }
    if (this.prefixStyle) {
      styles.push(this.prefixStyle);
    }
    return styles.join('; ');
  }

  get prefixContainerStyles(): string {
    const styles: string[] = [];
    if (this.prefixContainerStyle) {
      styles.push(this.prefixContainerStyle);
    }
    return styles.join('; ');
  }

    hasError(): boolean {
    if (!this._control) return false;

    // Check if there are actual errors
    const hasErrors = this._control.errors !== null;

    // Only show errors if the user has interacted with the field
    const hasBeenInteracted = this._control.dirty || this._control.touched;

    return hasErrors && hasBeenInteracted;
  }

    // Implement ControlValueAccessor methods
  writeValue(value: any): void {
    this.value = value;
    // Update the internal value when the form control value changes
    if (this._control && value !== this._control.value) {
      this._control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  // Reset the filtered options and search text when the dropdown is closed
 onSelectClosed() {
    if (this.searchText && this.filteredOptions.length === 0) {
      this.filteredOptions = [...this.options];
      this.searchText = '';
    }
  }
}

